import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  PlugZap, Save, Wifi, DownloadCloud, Trash2, ExternalLink, CheckCircle2, XCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const thisMonth = () => new Date().toISOString().slice(0, 7);

function StatusPill({ ok, children }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] mono px-2 py-0.5 rounded-sm ${ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
      {ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
      {children}
    </span>
  );
}

function MarketplaceCard({ mp, isAdmin, onChanged }) {
  const [enabled, setEnabled] = useState(mp.enabled);
  const [sandbox, setSandbox] = useState(mp.sandbox);
  const [vals, setVals] = useState(() => {
    const o = {};
    mp.fields.forEach((f) => { o[f.key] = f.secret ? "" : (f.value || ""); });
    return o;
  });
  const [busy, setBusy] = useState("");
  const [month, setMonth] = useState(thisMonth());
  const [count, setCount] = useState(60);
  const [orders, setOrders] = useState(true);
  const [payouts, setPayouts] = useState(true);

  const lastTest = mp.status?.last_test;
  const lastPull = mp.status?.last_pull;

  const save = async () => {
    setBusy("save");
    try {
      const config = {};
      const secrets = {};
      mp.fields.forEach((f) => {
        const v = vals[f.key];
        if (f.secret) { if (v) secrets[f.key] = v; }
        else { config[f.key] = v; }
      });
      await api.put(`/connections/${mp.marketplace}`, { enabled, sandbox, config, secrets });
      toast.success(`${mp.marketplace} connection saved`);
      // clear secret inputs after save (now stored masked)
      setVals((prev) => {
        const o = { ...prev };
        mp.fields.forEach((f) => { if (f.secret) o[f.key] = ""; });
        return o;
      });
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const test = async () => {
    setBusy("test");
    try {
      const { data } = await api.post(`/connections/${mp.marketplace}/test`);
      data.ok ? toast.success(data.message) : toast.error(data.message);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const pull = async () => {
    if (!orders && !payouts) { toast.error("Select orders, payouts or both"); return; }
    setBusy("pull");
    try {
      const { data } = await api.post(`/connections/${mp.marketplace}/pull`, {
        month, orders, payouts, count: Number(count) || 60,
      });
      const r = data.reconciliation;
      toast.success(
        `${mp.marketplace} ${data.mode} pull · ${data.orders || 0} orders · ${data.payouts || 0} payouts` +
        (r ? ` · ${r.variance} variances · ₹${(r.total_recoverable || 0).toLocaleString("en-IN")} recoverable` : "")
      );
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const clearData = async () => {
    if (!window.confirm(`Clear all pulled (sandbox) data for ${mp.marketplace}?`)) return;
    setBusy("clear");
    try {
      const { data } = await api.delete(`/connections/${mp.marketplace}/data`);
      toast.success(`Cleared ${data.sales_deleted} orders · ${data.settlement_deleted} payouts`);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="border border-border bg-white rounded-sm" data-testid={`conn-card-${mp.marketplace}`}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-sm bg-slate-900 text-white flex items-center justify-center text-[11px] font-bold">
            {mp.marketplace.slice(0, 2).toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-semibold tracking-tight">{mp.marketplace}</div>
            <div className="flex items-center gap-2 mt-0.5">
              {mp.doc_link ? (
                <a href={mp.doc_link} target="_blank" rel="noopener noreferrer" className="text-[11px] mono text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid={`conn-doc-${mp.marketplace}`}>
                  API docs <ExternalLink size={10} />
                </a>
              ) : <span className="text-[11px] mono text-slate-400">no public docs</span>}
              <span className={`text-[10px] mono uppercase px-1.5 py-0.5 rounded-sm ${sandbox ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`}>
                {sandbox ? "Sandbox" : "Live"}
              </span>
              <span className={`text-[10px] mono uppercase px-1.5 py-0.5 rounded-sm ${enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {enabled ? "Enabled" : "Disabled"}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {mp.fields.map((f) => (
            <label key={f.key} className="block">
              <div className="overline mb-1">
                {f.label}{f.required ? <span className="text-rose-500"> *</span> : null}
                {f.secret && f.set ? <span className="text-[10px] text-emerald-600 ml-1">set {f.masked}</span> : null}
              </div>
              <input
                data-testid={`conn-field-${mp.marketplace}-${f.key}`}
                type={f.secret ? "password" : "text"}
                disabled={!isAdmin}
                value={vals[f.key]}
                placeholder={f.secret ? (f.set ? "•••••••• (leave blank to keep)" : "enter value") : ""}
                onChange={(e) => setVals((p) => ({ ...p, [f.key]: e.target.value }))}
                className="w-full border border-border rounded-sm px-2.5 py-1.5 text-sm mono focus:outline-none focus:border-slate-500 disabled:bg-slate-50"
              />
            </label>
          ))}
        </div>

        <div className="flex items-center gap-4 flex-wrap text-sm">
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`conn-toggle-enabled-${mp.marketplace}`}>
            <input type="checkbox" disabled={!isAdmin} checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span>Enabled</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`conn-toggle-sandbox-${mp.marketplace}`}>
            <input type="checkbox" disabled={!isAdmin} checked={sandbox} onChange={(e) => setSandbox(e.target.checked)} />
            <span>Sandbox mode <span className="text-xs text-slate-400">(preview with simulated data)</span></span>
          </label>
          {isAdmin && (
            <div className="ml-auto flex items-center gap-2">
              <button data-testid={`conn-save-${mp.marketplace}`} onClick={save} disabled={busy} className="btn">
                <Save size={12} /> {busy === "save" ? "Saving…" : "Save"}
              </button>
              <button data-testid={`conn-test-${mp.marketplace}`} onClick={test} disabled={busy} className="btn">
                <Wifi size={12} /> {busy === "test" ? "Testing…" : "Test"}
              </button>
            </div>
          )}
        </div>

        {(lastTest || lastPull) && (
          <div className="flex items-center gap-2 flex-wrap text-xs mono text-slate-500 border-t border-border pt-3">
            {lastTest && <StatusPill ok={lastTest.ok}>test: {lastTest.message}</StatusPill>}
            {lastPull && <StatusPill ok={lastPull.ok}>pull: {lastPull.ok ? `${lastPull.orders || 0} orders · ${lastPull.payouts || 0} payouts` : lastPull.message}</StatusPill>}
          </div>
        )}

        {isAdmin && (
          <div className="border-t border-border pt-4">
            <div className="overline mb-2">Pull data</div>
            <div className="flex items-end gap-3 flex-wrap">
              <label className="block">
                <div className="overline mb-1">Month</div>
                <input data-testid={`conn-pull-month-${mp.marketplace}`} value={month} onChange={(e) => setMonth(e.target.value)} placeholder="YYYY-MM" className="border border-border rounded-sm px-2.5 py-1.5 text-sm mono w-28" />
              </label>
              {sandbox && (
                <label className="block">
                  <div className="overline mb-1">Rows (sandbox)</div>
                  <input data-testid={`conn-pull-count-${mp.marketplace}`} type="number" value={count} onChange={(e) => setCount(e.target.value)} className="border border-border rounded-sm px-2.5 py-1.5 text-sm mono w-24" />
                </label>
              )}
              <label className="inline-flex items-center gap-2 text-sm pb-1.5">
                <input type="checkbox" checked={orders} onChange={(e) => setOrders(e.target.checked)} /> Orders
              </label>
              <label className="inline-flex items-center gap-2 text-sm pb-1.5">
                <input type="checkbox" checked={payouts} onChange={(e) => setPayouts(e.target.checked)} /> Payouts
              </label>
              <button data-testid={`conn-pull-${mp.marketplace}`} onClick={pull} disabled={busy} className="btn bg-slate-900 text-white hover:bg-slate-800 border-slate-900">
                <DownloadCloud size={12} /> {busy === "pull" ? "Pulling…" : "Pull now"}
              </button>
              <button data-testid={`conn-clear-${mp.marketplace}`} onClick={clearData} disabled={busy} className="btn btn-danger">
                <Trash2 size={12} /> Clear pulled
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Connections() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [marketplaces, setMarketplaces] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/connections");
      setMarketplaces(data.marketplaces || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  return (
    <div className="p-6 space-y-5" data-testid="connections-page">
      <div>
        <div className="overline">Integrations</div>
        <h1 className="text-2xl font-semibold tracking-tight mt-1 text-slate-900 flex items-center gap-2">
          <PlugZap size={22} strokeWidth={1.6} /> Marketplace Connections
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Configure each marketplace&apos;s API credentials to pull orders &amp; payouts directly. Secrets are encrypted at rest and shown masked.
          Keep <span className="font-medium">Sandbox mode</span> on to preview the full ingest → reconcile flow with representative data; switch to
          <span className="font-medium"> Live</span> once the marketplace has granted API access and you&apos;ve saved valid credentials.
        </p>
        {!isAdmin && <p className="text-xs text-amber-600 mt-2">You have read-only access. Ask an admin to edit connections.</p>}
      </div>

      {loading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {marketplaces.map((mp) => (
            <MarketplaceCard key={mp.marketplace} mp={mp} isAdmin={isAdmin} onChanged={refresh} />
          ))}
        </div>
      )}
    </div>
  );
}
